// type FlashType = "error" | "success" | "info";

// type StringMessage = string;
// type GraphqlExceptionMessage = {
//   message: StringMessage,
//   source: {
//     type: string,
//     errors: Array<{message: StringMessage}>
//   }
// };
// type Message = GraphqlExceptionMessage | StringMessage;

// export type FlashItem = {
//   id: string, // UUID from crypto.randomUUID(), or the 'FLASH_CONN_ERROR' sentinel
//   type: FlashType,
//   message: StringMessage | React.Node
// };
//
// The id is opaque: consumers use it only as a React key and for equality checks
// (removal, and matching the 'FLASH_CONN_ERROR' sentinel). Nothing relies on its
// shape or ordering, so the switch from numeric timestamps to UUIDs is safe.

import EventEmitter from 'eventemitter3';

// Bounds for the provider-less flash buffer. Flashes raised while no provider is
// mounted are buffered and replayed on the next mount; these limits stop a stale
// message surfacing later on an unrelated screen (TTL) and cap worst-case memory
// (size). A flash for an imminent redirect is drained within one render cycle, so
// it stays well inside the TTL.
export const FLASH_BUFFER_TTL_MS = 5000;
export const FLASH_BUFFER_MAX_SIZE = 5;

class FlashesStore extends EventEmitter {
  constructor(...args) {
    super(args);
    this.preloaded = [];
    this.buffer = [];
    this.ERROR = 'error';
    this.INFO = 'info';
    this.SUCCESS = 'success';
    this.WARNING = 'warning';
  }

  preload(flashes) {
    flashes.forEach((flash) => {
      this.preloaded.push({ id: crypto.randomUUID(), ...flash });
    });
  }

  flash(type, message, mutationName) {
    let returnMessage = message;

    const { message: content, source } = message;

    // See if the message is actually a GraphQL exception
    if (content && source) {
      const { errors, type: sourceType } = source;
      // $FlowExpectError
      if (errors?.length && errors[0] && errors[0].message) {
        if (sourceType === 'unknown_error') {
          returnMessage = 'Sorry, there’s been an unexpected error and our engineers have been notified';
        } else {
          returnMessage = errors[0].message;
          if (errors[0].path) {
            const { path } = errors[0];
            returnMessage = `${path[path.length - 1]}: ${returnMessage}`;
          }
        }
      } else {
        returnMessage = 'An unknown error occured';
      }
    }

    const payload = {
      id: crypto.randomUUID(), type, message: returnMessage, mutationName,
    };

    // When no listener is subscribed (e.g. a flash is raised before the
    // FlashesProvider has registered its 'flash' handler), buffer the payload so
    // it can be replayed once a provider mounts, instead of emitting it to zero
    // listeners and losing it. Stamp it so drainBuffer can drop stale entries,
    // and cap the buffer to bound memory by dropping the oldest.
    if (this.listenerCount('flash') === 0) {
      this.buffer.push({ ...payload, bufferedAt: Date.now() });
      if (this.buffer.length > FLASH_BUFFER_MAX_SIZE) {
        this.buffer.splice(0, this.buffer.length - FLASH_BUFFER_MAX_SIZE);
      }
    } else {
      this.emit('flash', payload);
    }
  }

  // Return and clear the buffer of live flashes that were raised while no
  // listener was subscribed. The swap is atomic so a second caller receives an
  // empty array and messages are never replayed twice. Entries older than the
  // TTL are discarded so a stale flash never surfaces on an unrelated screen, and
  // the internal bufferedAt stamp is stripped from the returned payloads.
  drainBuffer() {
    const now = Date.now();
    const pending = this.buffer;
    this.buffer = [];
    return pending
      .filter((entry) => now - entry.bufferedAt <= FLASH_BUFFER_TTL_MS)
      .map((entry) => {
        const payload = { ...entry };
        delete payload.bufferedAt;
        return payload;
      });
  }

  reset() {
    this.buffer = [];
    this.preloaded = [];
    this.emit('reset');
  }
}

export default new FlashesStore();
